import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js'
import { StdioServerTransport } from '@modelcontextprotocol/sdk/server/stdio.js'
import { z } from 'zod'

type DiagnosticClient = { getJson<T>(pathname: string): Promise<T> }

const MAX_TEXT_CHARS = 24_000

function truncateValue(value: unknown, state: { truncated: boolean }): unknown {
  if (typeof value === 'string') {
    if (value.length <= MAX_TEXT_CHARS) return value
    state.truncated = true
    return value.slice(0, MAX_TEXT_CHARS)
  }
  if (Array.isArray(value)) return value.map(item => truncateValue(item, state))
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([key, item]) => [key, truncateValue(item, state)]))
  }
  return value
}

export function boundMcpResult(value: unknown): Record<string, unknown> {
  const state = { truncated: false }
  const bounded = truncateValue(value, state)
  if (bounded && typeof bounded === 'object' && !Array.isArray(bounded)) return { ...(bounded as Record<string, unknown>), truncated: state.truncated }
  return { data: bounded, truncated: state.truncated }
}

function textResult(value: unknown) {
  return { content: [{ type: 'text' as const, text: JSON.stringify(boundMcpResult(value)) }] }
}

export function createDiagnosticsMcpServer(client: DiagnosticClient): McpServer {
  const server = new McpServer(
    { name: 'jujing-novel-diagnostics', version: '0.1.0' },
    { capabilities: { tools: {} }, instructions: 'Read-only Novel diagnostics. Use CLI exports for full local evidence.' },
  )
  server.tool('novel.project.inspect', 'Read a bounded project diagnostics overview.', {
    project_id: z.string().min(1),
  }, async ({ project_id }) => textResult(await client.getJson(`/api/skill-debug/diagnostics/projects/${encodeURIComponent(project_id)}/inspect`)))
  server.tool('novel.message.diagnose', 'Read one assistant message diagnostic. Raw tool payloads require include_raw=true.', {
    message_id: z.string().min(1), include_raw: z.boolean().optional(),
  }, async ({ message_id, include_raw }) => {
    const raw = include_raw ? '?include_raw=true' : ''
    return textResult(await client.getJson(`/api/skill-debug/diagnostics/messages/${encodeURIComponent(message_id)}${raw}`))
  })
  server.tool('novel.analysis.inspect', 'Read bounded blockbuster-analysis metadata and product index.', {
    analysis_id: z.string().min(1),
  }, async ({ analysis_id }) => textResult(await client.getJson(`/api/skill-debug/diagnostics/analyses/${encodeURIComponent(analysis_id)}`)))
  server.tool('novel.diagnostic.run', 'Read bounded immutable QA run evidence.', {
    run_id: z.string().min(1),
  }, async ({ run_id }) => textResult(await client.getJson(`/api/skill-debug/diagnostics/runs/${encodeURIComponent(run_id)}`)))
  return server
}

export async function startDiagnosticsMcpServer(client: DiagnosticClient): Promise<void> {
  await createDiagnosticsMcpServer(client).connect(new StdioServerTransport())
}
